// @ts-strict-ignore
import { ProjectModel } from '../../../model/project.model';
import { UndoRedoHelper } from '@shared/undo/undo-redo.helper';
import { ArticleManager } from '@article/manager/article.manager';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { SuitableConnectionsSetVO } from '../model/vo/suitable-connections-set.vo';
import { SuitableConnectionsVO } from '../model/vo/suitable-connections.vo';
import { Dictionary } from 'typescript-collections';
import { Vector3 } from 'three';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { IOperation } from '@shared/undo/operation/i-operation';
import { ConnectionCubeSearchVector } from '@article/components/connections/connection-creation/search/connection-cube-search-vector';
import { ArrayUtils } from '@shared/util/array-utils';
import { SuitableConnectionAreasVO } from '../model/vo/suitable-connection-areas.vo';
import { Connection } from '@3d-scene/model3d/connection';
import { ConnectionAreaVO, MetaElementVO } from '@shared/vo/meta-element.vo';
import { ConnectionCubeSearchRequirements } from '@article/components/connections/connection-creation/search/connection-cube-search-requirements';
import { ConnectionCubeSearchHelper } from '@article/components/connections/connection-creation/search/connection-cube-search.helper';
import { ArticlePrepareData } from '../processor/prepare/insert/article-prepare-data';
import { ArticlePrepareProcessorRegistry } from '../processor/prepare/article-prepare-processor-registry';
import { LoadConnectionTextureProcessor } from '@3d-scene/processor/load-connection-texture.processor';
import { ICreateArticleOperation } from '../processor/prepare/i-create-article-operation';
import { IArticlePrepareProcessor } from '../processor/prepare/i-article-prepare.processor';
import { NumberUtils } from '@shared/util/number-utils';
import { DefaultSingleConnectionPrepareProcessor } from '../processor/prepare/default-single-connection-prepare.processor';
import { ConnectArticlePrepareData } from '../processor/prepare/insert/connect-article-prepare-data';
import { InsertArticlePrepareData } from '../processor/prepare/insert/insert-article-prepare-data';
import { InsertArticleOperation } from '../processor/prepare/insert-article-operation';
import { ConnectArticleOperation } from '../processor/prepare/connect-article-operation';
import { UndoAttachArticlesOperation } from '@shared/undo/operation/undo-attach-articles-operation';
import { UndoRedoUtils } from '@shared/undo/undo-redo-utils';
import { BaseAdvancedOperation } from '@shared/undo/operation/base-advanced-operation';
import { UndoDetachArticlesOperation } from '@shared/undo/operation/undo-detach-articles-operation';
import { UndoInsertArticlesByLocationOperation } from '@shared/undo/operation/undo-insert-articles-by-location-operation';
import { UndoReplaceArticlesByLocationOperation } from '@shared/undo/operation/undo-replace-articles-by-location-operation';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { TextureConnectionUtils } from '@3d-scene/processor/texture-connection-utils';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { AlertHelper } from '@shared/alert/alert.helper';
import { DirectPrintingHelper } from '@article/helper/direct-printing/direct-printing.helper';
import { ArticleUtils } from '@article/util/article-utils';
import { ArticleConnectProcessorRegistry } from '../processor/prepare/connect/article-connect-processor-registry';
import { OpenLabelEditorHelper } from '../../label/label-editor/service/open-label-editor.helper';
import { InsertArticleHelperFactory } from '@article/helper/insert-article-helper.factory';
import { InsertArticleUtils } from '../util/insert-article-utils';
import { Log } from '@shared/debug/log';
import { ArticleCreationService } from '../service/article-creation.service';
import { InsertArticleService } from '../service/insert-article.service';
import { ProjectArticleService } from '@project/service/project-article.service';
import { SelectionModelService } from '../service/selection-model.service';
import { DetachArticleUsageService } from '@project/service/detach-article-usage.service';
import { MetaElementUtils } from '@article/components/connections/connection-creation/meta-element.utils';
import { ConnectionInfoService } from '@3d-scene/model3d/service/connection-info.service';
import { lastValueFrom } from 'rxjs';
import { ChangeLtlArticleService } from '@article/ltl/change-ltl-article.service';

export class ArticleCreationManager {
  private static readonly DEFAULT_SUCCESS_MESSAGE: string = 'articlesAdded';

  public messageType: string = ArticleCreationManager.DEFAULT_SUCCESS_MESSAGE;

  public showFaultMessage: boolean = true;

  private _currentCreateOperation: ICreateArticleOperation;

  public createUndoOperation: boolean = true;
  public detachArticleUsageIds: string[] = [];
  private _replaceArticleUsageIds: string[] = [];
  public get replaceArticleUsageIds(): string[] {
    return this._replaceArticleUsageIds;
  }
  public set replaceArticleUsageIds(usageIds: string[]) {
    ArrayUtils.mergeArrays(this.detachArticleUsageIds, usageIds || []);
    this._replaceArticleUsageIds = usageIds || [];
  }
  public checkFreeConnection: boolean = true;
  public removeSelection: boolean = true;
  public isUnusedLabeling: boolean = false;

  private _articleHead: ArticleVO;
  private _givingConnectionSets: SuitableConnectionsSetVO[];
  private _acceptingCubeVectors: ConnectionCubeSearchVector[];

  private _suitableConnectionsToConnect: SuitableConnectionsVO;

  private _loadingArticleId: number;
  private _skipAccessoryTestForArticleId: number;

  private _articleToConnect: ArticleVO;
  private _connectPrepareData: ArticlePrepareData[];
  private _insertPrepareData: ArticlePrepareData[];
  private _moveArticlesMap: Dictionary<string, Vector3>;
  private _createdArticleContainers: ArticleContainer[] = [];
  private _createdUndoOperations: IOperation[] = null;

  constructor(
    private alertHelper: AlertHelper,
    private projectModel: ProjectModel,
    private projectArticleService: ProjectArticleService,
    private selectionModelService: SelectionModelService,
    private undoRedoHelper: UndoRedoHelper,
    private articleManager: ArticleManager,
    private prepareProcessorRegistry: ArticlePrepareProcessorRegistry,
    private loadConnectionTextureProcessor: LoadConnectionTextureProcessor,
    private localeResourceManager: LocaleResourceManager,
    private directPrintingHelper: DirectPrintingHelper,
    private connectProcessorRegistry: ArticleConnectProcessorRegistry,
    private openLabelEditorHelper: OpenLabelEditorHelper,
    private undoRedoUtils: UndoRedoUtils,
    private containerService: ContainerService,
    private connectionInfoService: ConnectionInfoService,
    private textureConnectionUtils: TextureConnectionUtils,
    private connectionCubeSearchHelper: ConnectionCubeSearchHelper,
    private insertArticleHelperFactory: InsertArticleHelperFactory,
    private insertArticleUtils: InsertArticleUtils,
    private articleCreationService: ArticleCreationService,
    private insertArticleService: InsertArticleService,
    private detachArticleUsageService: DetachArticleUsageService,
    private changeLtlArticleService: ChangeLtlArticleService,
  ) {
  }

  public get createdUndoOperations(): IOperation[] {
    return this._createdUndoOperations;
  }

  public async createArticlesBySuitableConnections(
    suitableGivingConnections: SuitableConnectionsVO,
    acceptingCubeVectors: ConnectionCubeSearchVector[],
    skipAccessoryTest: boolean = false
  ): Promise<void> {
    // TODO: (from flex) do we need to clear some fields (e.g. _createdArticleContainers, detachArticleUsageIds)
    // if creation fails and user tries to connect article again?
    if (suitableGivingConnections) {
      this._articleHead = suitableGivingConnections.article;
      if (suitableGivingConnections.connectionSets) {
        this._givingConnectionSets = suitableGivingConnections.connectionSets.concat();
      }
    }
    this._acceptingCubeVectors = acceptingCubeVectors;

    this.prepareSuitableConnectionsToConnect();
    await this.loadArticleToConnect(skipAccessoryTest);
  }


  private prepareSuitableConnectionsToConnect(): void {
    const suitableConnections: SuitableConnectionsVO = new SuitableConnectionsVO();
    suitableConnections.article = this._articleHead;
    suitableConnections.connectionSets = this.getConnectionSetsToProcessing(this._givingConnectionSets);
    this._suitableConnectionsToConnect = suitableConnections;

    ArrayUtils.removeAllItemsInArray(this._givingConnectionSets, this._suitableConnectionsToConnect.connectionSets);
  }

  private getConnectionSetsToProcessing(connectionSets: SuitableConnectionsSetVO[]): SuitableConnectionsSetVO[] {
    const result: SuitableConnectionsSetVO[] = [];
    if (!connectionSets) {
      return result;
    }
    const maxPermittedConnectionSet: SuitableConnectionsSetVO = this.getMaxPermittedConnectionSet(connectionSets);
    if (!maxPermittedConnectionSet) {
      return result;
    }
    for (const connectionSet of connectionSets) {
      if (this.isEqualsNamesConnectionSets(maxPermittedConnectionSet.connectionNames, connectionSet.connectionNames)) {
        result.push(connectionSet);
      }
    }
    return result;
  }

  private getMaxPermittedConnectionSet(connectionSets: SuitableConnectionsSetVO[]): SuitableConnectionsSetVO {
    if (!connectionSets) {
      return null;
    }
    let result: SuitableConnectionsSetVO;
    for (const connectionSet of connectionSets) {
      if (!result || connectionSet.connectionNames.length > result.connectionNames.length) {
        result = connectionSet;
      }
    }
    return result;
  }

  private isEqualsNamesConnectionSets(sourceNames: string[], targetNames: string[]): boolean {
    return sourceNames.indexOf(targetNames[0]) > -1;
  }

  public async createArticlesByAccessoryArticleAndAcceptingCubes(accessoryArticle: ArticleVO, acceptingCubes: Connection[], skipAccessoryTest: boolean = false): Promise<void> {
    const givingAreas: ConnectionAreaVO[] = ArticleUtils.getConnectionElements(accessoryArticle, ConnectionAreaVO.SIDE_GIVING);
    const suitableConnection: SuitableConnectionAreasVO = SuitableConnectionAreasVO.create(accessoryArticle, givingAreas);
    await this.createArticlesBySuitableGivingConnectionAreasAndAcceptingCubes(suitableConnection, acceptingCubes, skipAccessoryTest);
  }

  public async createArticlesBySuitableGivingConnectionAreasAndAcceptingCubes(suitableGivingAreas: SuitableConnectionAreasVO,
    acceptingCubes: Connection[], skipAccessoryTest: boolean = false): Promise<void> {
    const suitableConnections: SuitableConnectionsVO = new SuitableConnectionsVO();
    suitableConnections.article = suitableGivingAreas.article;
    suitableConnections.connectionSets = [];
    const connectionsInSets: Dictionary<string, MetaElementVO[]> = MetaElementUtils.getConnectionsInSets(suitableGivingAreas.connectionAreas);
    let suitableConnectionsSet: SuitableConnectionsSetVO;
    const searchVectorId: number = 1;
    for (const connectionSet of connectionsInSets.values()) {
      suitableConnectionsSet = new SuitableConnectionsSetVO();
      suitableConnectionsSet.searchVectorId = searchVectorId;
      const connectionNames: string[] = [];
      for (const connectionArea of connectionSet) {
        connectionNames.push(connectionArea.name);
      }
      suitableConnectionsSet.connectionNames = connectionNames;
      suitableConnectionsSet.matchingRequirementIndices = ArrayUtils.createIntSequenceArray(0, connectionNames.length - 1);
      suitableConnections.connectionSets.push(suitableConnectionsSet);
    }

    const connectionCubeSearchRequirementsList: ConnectionCubeSearchRequirements[] = [];
    let globalRequirementIndex: number = 0;
    for (const acceptingCube of acceptingCubes) {
      const connectionCubeSearchRequirements: ConnectionCubeSearchRequirements = new ConnectionCubeSearchRequirements(
        this.connectionInfoService,
        acceptingCube
      );
      connectionCubeSearchRequirements.globalRequirementIndex = globalRequirementIndex;
      connectionCubeSearchRequirements.connectionRequirements = this.connectionInfoService.findRequirementPropertiesByConnection(acceptingCube);
      globalRequirementIndex++;
      connectionCubeSearchRequirementsList.push(connectionCubeSearchRequirements);
    }
    // TODO: (from flex) consideration of range-on-line (freedom) connections might be needed here
    // (e.g. creation of separate search-vector might be needed)
    // at the moment this method is used in: proServe-import, cae-import (in regard to accessories and jumpers)
    const acceptingCubeVector: ConnectionCubeSearchVector = new ConnectionCubeSearchVector(searchVectorId,
      connectionCubeSearchRequirementsList, acceptingCubes.length > 1);
    await this.createArticlesBySuitableConnections(suitableConnections, [acceptingCubeVector], skipAccessoryTest);
  }

  private async loadArticleToConnect(skipAccessoryTest: boolean = false): Promise<void> {
    const article: ArticleVO = this._suitableConnectionsToConnect.article;
    this._loadingArticleId = article.identity;
    if (skipAccessoryTest) {
      this._skipAccessoryTestForArticleId = article.identity;
    }

    const fullArticle: ArticleVO = this.articleManager.getCachedArticle(article.identity, article.version);
    if (fullArticle) {
      return this.fullArticleLoaded(fullArticle);
    }

    const loadedArticle = await lastValueFrom(this.articleManager.newLoadArticle_handler(article.identity, article.version));
    if (loadedArticle) {
      return this.fullArticleLoaded(loadedArticle);
    }
    this.loadArticle_failure();
    throw new Error('Could not load article');
  }

  private async fullArticleLoaded(article: ArticleVO): Promise<void> {
    if (!this._suitableConnectionsToConnect) {
      Log.info(`Cannot load full article: No suitable connections to connect for article no. ${article?.articleNo}`)
      return;
    }
    let prepareArticleDatas: ArticlePrepareData[];
    this._articleToConnect = article;

    const articleToDeterminePrepareProcessor: ArticleVO = !this._articleToConnect.baseArticle
      ? this._articleToConnect
      : this._articleToConnect.baseArticle;
    const connectionSetId: string = this.connectionCubeSearchHelper.getConnectionSetIdBySuitableSet(
      this._suitableConnectionsToConnect.connectionSets[0],
      articleToDeterminePrepareProcessor
    );
    const prepareProcessor: IArticlePrepareProcessor = this.prepareProcessorRegistry.get(articleToDeterminePrepareProcessor, connectionSetId);
    if (!prepareProcessor) {
      Log.info(`Cannot load full article: Could not get prepare processor for article no. ${article?.articleNo}`)
      return;
    }
    if (
      NumberUtils.isNumber(this._skipAccessoryTestForArticleId) &&
      this._skipAccessoryTestForArticleId === this._articleToConnect.identity &&
      prepareProcessor instanceof DefaultSingleConnectionPrepareProcessor
    ) {
      prepareArticleDatas = (prepareProcessor as DefaultSingleConnectionPrepareProcessor).forceProcess(
        this._articleToConnect,
        this._suitableConnectionsToConnect.connectionSets,
        this._acceptingCubeVectors,
        this.checkFreeConnection
      );
    } else {
      prepareArticleDatas = prepareProcessor.process(
        this._articleToConnect,
        this._suitableConnectionsToConnect.connectionSets,
        this._acceptingCubeVectors,
        this.checkFreeConnection
      );
    }
    this._insertPrepareData = [];
    this._connectPrepareData = [];
    for (const prepareData of prepareArticleDatas) {
      if (prepareData instanceof ConnectArticlePrepareData) {
        this._connectPrepareData.push(prepareData);
      } else if (prepareData instanceof InsertArticlePrepareData) {
        this._insertPrepareData.push(prepareData);
      }
    }

    // after processing accepting cube vector in prepareProcessor we could get empty result here.
    // this happens when sending cubes from cae processor in case articles with multiple giving connections are connected.
    if (this._insertPrepareData.length === 0 && this._connectPrepareData.length === 0) {
      if (this._givingConnectionSets && this._givingConnectionSets.length !== 0) {
        await this.processNextConnectionSet();
      } else {
        await this.finalizeArticlesCreation();
      }
    } else {
      await this.executeInsertOperation();
    }
  }

  private async executeInsertOperation(): Promise<void> {
    if (this._insertPrepareData.length > 0) {
      this._currentCreateOperation = new InsertArticleOperation(
        this.projectModel,
        this.openLabelEditorHelper,
        this.containerService,
        this.insertArticleHelperFactory,
        this.insertArticleService
      );
      (this._currentCreateOperation as InsertArticleOperation).usageIdsToReplace = this._replaceArticleUsageIds;

      try {
        const result = await this._currentCreateOperation.execute(this._articleToConnect, this._insertPrepareData, this._suitableConnectionsToConnect, null);
        await this.insertOperationCompleted(result.removeSelection)
      } catch (error) {
        this.insertArticle_failure(error)
      }
    } else {
      await this.executeConnectOperation();
    }
  }

  private async executeConnectOperation(): Promise<void> {
    if (this._connectPrepareData.length > 0) {
      const accConnections = [];
      this._connectPrepareData.forEach(pd => pd instanceof ConnectArticlePrepareData && ArrayUtils.mergeArrays(accConnections, pd.acceptingConnections));
      await lastValueFrom(this.directPrintingHelper.updateDirectPrintingOnConnectAccessoryOperation(accConnections));
      this._currentCreateOperation = new ConnectArticleOperation(
        this.isUnusedLabeling,
        this.connectProcessorRegistry,
        this.connectionCubeSearchHelper
      );
      try {
        const result = await this._currentCreateOperation.execute(this._articleToConnect, this._connectPrepareData, this._suitableConnectionsToConnect, this.detachArticleUsageIds);
        await this.connectOperationCompleted(result.removeSelection);
      } catch (error) {
        await this.connectArticle_failure(error)
      }
    } else {
      await this.finalizeArticlesCreation();
    }
  }

  private mergeDetachUsageIds(sourceIds: string[]): void {
    if (!sourceIds) {
      return;
    }
    if (!this.detachArticleUsageIds) {
      this.detachArticleUsageIds = [];
    }
    ArrayUtils.mergeArrays(this.detachArticleUsageIds, sourceIds);
  }

  private async detachConnectedArticles(usageIds: string[]): Promise<void> {
    if (this.createUndoOperation && isNaN(this._skipAccessoryTestForArticleId)) {
      await this.pushUndoArticlesOperation(this.getCreatedArticleContainers(), usageIds, this._createdUndoOperations);
    }

    if (usageIds) {
      await this.detachArticleUsageService.detachArticleUsageFromProjectByIds(usageIds);
    }
  }

  private async pushUndoArticlesOperation(articleContainers: ArticleContainer[], usageIdsToDetach: string[], createdUndoOperations: IOperation[]): Promise<void> {
    if (!articleContainers) {
      return;
    }
    const attachOperation: UndoAttachArticlesOperation = this.undoRedoUtils.createUndoAttachOperationByArticleContainers(articleContainers);
    if (!attachOperation) {
      return;
    }
    let articlesOperation: BaseAdvancedOperation;
    const detachOperation: UndoDetachArticlesOperation = this.createDetachOperation(usageIdsToDetach);
    if (!detachOperation) {
      articlesOperation = new UndoInsertArticlesByLocationOperation(attachOperation, this._moveArticlesMap, this.insertArticleUtils);
    } else {
      articlesOperation = new UndoReplaceArticlesByLocationOperation(
        attachOperation,
        detachOperation,
        this._moveArticlesMap,
        this.insertArticleUtils
      );
    }
    articlesOperation.additionalOperations = createdUndoOperations;
    await this.undoRedoHelper.pushUndoOperation(articlesOperation);
  }

  private createDetachOperation(detachUsageIds: string[]): UndoDetachArticlesOperation {
    if (!detachUsageIds || detachUsageIds.length === 0) {
      return null;
    }
    const articlesToDetach: ArticleContainer[] = [];
    for (const usageId of detachUsageIds) {
      const articleContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(usageId);
      if (!articleContainer) {
        continue;
      }
      articlesToDetach.push(articleContainer);
      const connectedContainers: ArticleContainer[] = this.projectArticleService.getConnectedArticlesWithRelative(articleContainer);
      ArrayUtils.mergeArrays(articlesToDetach, connectedContainers);
    }
    return this.undoRedoUtils.createUndoDetachOperationByArticleContainers(articlesToDetach);
  }

  public getCreatedArticleContainers(): ArticleContainer[] {
    return this._createdArticleContainers;
  }

  private storeCreateOperationResult(): void {
    const createdArticles: ArticleContainer[] = this._currentCreateOperation.createdArticleContainers;
    if (createdArticles && createdArticles.length > 0) {
      this._createdArticleContainers = this._createdArticleContainers.concat(createdArticles);
    }
    this.mergeMoveArticlesMap(this._currentCreateOperation.moveArticlesMap);
    this.mergeDetachUsageIds(this._currentCreateOperation.usageIdsToDetach);
    this.concatCreatedUndoOperations(this._currentCreateOperation.createdUndoOperations);
  }

  private mergeMoveArticlesMap(moveMap: Dictionary<string, Vector3>): void {
    if (!moveMap) {
      return;
    }
    if (!this._moveArticlesMap) {
      this._moveArticlesMap = new Dictionary<string, Vector3>();
    }
    InsertArticleUtils.mergeMoveMap(this._moveArticlesMap, moveMap);
  }

  private concatCreatedUndoOperations(undoOperations: IOperation[]): void {
    if (!undoOperations) {
      return;
    }
    if (!this._createdUndoOperations) {
      this._createdUndoOperations = [];
    }
    this._createdUndoOperations = this._createdUndoOperations.concat(undoOperations);
  }

  private async insertOperationCompleted(removeSelection: boolean): Promise<void> {
    if (this._currentCreateOperation) {
      this.removeSelection = removeSelection;
      this.storeCreateOperationResult();
      this.removeCurrentCreateOperationFromContext();
      await this.executeConnectOperation();
    }
  }

  private async connectOperationCompleted(removeSelection: boolean): Promise<void> {
    if (this._currentCreateOperation) {
      this.removeSelection = removeSelection;
      if (!this.isUnusedLabeling) {
        const undoCleanTextureOperation: IOperation = this.textureConnectionUtils.cleanMultipurposeTextureConnections(
          this._currentCreateOperation.createdArticleContainers,
          this.loadConnectionTextureProcessor
        );
        if (undoCleanTextureOperation) {
          this.concatCreatedUndoOperations([undoCleanTextureOperation]);
        }
      }
      this.storeCreateOperationResult();
      this.removeCurrentCreateOperationFromContext();
      await this.finalizeArticlesCreation();
    }
  }

  private async finalizeArticlesCreation(): Promise<void> {
    await this.undoRedoHelper.doWithSequence(async () => {
      await this.detachConnectedArticles(this.detachArticleUsageIds);
      if (this.removeSelection) {
        this.selectionModelService.removeAllSelections();
      }
      const createdArticleUsages = this.getCreatedArticleUsages();
      this.articleCreationService.articlesAdded(this.messageType, this._loadingArticleId, createdArticleUsages);
      await this.changeLtlArticleService.articlesAdded(createdArticleUsages);
      if (!this.createUndoOperation) {
        this.concatCreatedUndoOperations(this.undoRedoHelper.popAllFromRunningSequence());
      }
    });
  }

  private async connectArticle_failure(error: Error): Promise<void> {
    if (!this._currentCreateOperation) {
      return;
    }
    if (this._givingConnectionSets.length > 0) {
      await  this.processNextConnectionSet();
    } else {
      this.processingFailureConnectArticle(error.message);
    }
  }

  private insertArticle_failure(error: Error): void {
    if (!this._currentCreateOperation) {
      return;
    }
    this.processingFailureConnectArticle(error.message);
  }

  private processingFailureConnectArticle(errorMessage: string): void {
    this.removeCurrentCreateOperationFromContext();

    const articleToConnectNo: string = this._articleToConnect.articleNo;
    const loadingArticleId: number = this._loadingArticleId;

    this.clean();

    this.articleCreationService.articlesAddFailure(this.messageType, loadingArticleId);

    if (this.showFaultMessage) {
      const title: string = this.localeResourceManager.getValueByKey('articleCreationManager.title.error_connect_accessory');
      const message: string = this.localeResourceManager.getValueByKey('articleCreationManager.message.error_connect_accessory', {
        '0': articleToConnectNo,
        '1': errorMessage,
      });
      this.alertHelper.showError(title, message);
    }
  }

  private processNextConnectionSet(): Promise<void> {
    this.prepareSuitableConnectionsToConnect();
    return this.loadArticleToConnect();
  }

  private loadArticle_failure(): void {
    this.removeCurrentCreateOperationFromContext();

    const loadingArticleId: number = this._loadingArticleId;

    this.clean();

    if (NumberUtils.isNumber(loadingArticleId)) {
      this.articleCreationService.articlesAddFailure(this.messageType, loadingArticleId)
      if (this.showFaultMessage) {
        const title: string = this.localeResourceManager.getValueByKey('articleCreationManager.title.error_load_article');
        const message: string = this.localeResourceManager.getValueByKey('articleCreationManager.message.error_load_article', [loadingArticleId]);
        this.alertHelper.showError(title, message);
      }
    }
  }

  private removeCurrentCreateOperationFromContext(): void {
    this._currentCreateOperation?.clear();
    this._currentCreateOperation = null;
  }

  public getCreatedArticleUsages(): ArticleUsageVO[] {
    if (!this._createdArticleContainers) {
      return [];
    }
    const articleUsages: ArticleUsageVO[] = [];
    for (const container of this._createdArticleContainers) {
      articleUsages.push(container.articleUsage);
    }
    return articleUsages;
  }

  public clean(): void {
    this.detachArticleUsageIds = [];
    this.removeSelection = true;

    this._articleHead = null;
    this._givingConnectionSets = null;
    this._suitableConnectionsToConnect = null;
    this._acceptingCubeVectors = [];

    this._loadingArticleId = NaN;
    this._skipAccessoryTestForArticleId = NaN;

    this._articleToConnect = null;
    this._createdArticleContainers = [];
    this._createdUndoOperations = null;

    this._currentCreateOperation?.clear();
    this._currentCreateOperation = null;
    this._connectPrepareData = [];
    this._insertPrepareData = [];
    this._moveArticlesMap = null;

    this.messageType = ArticleCreationManager.DEFAULT_SUCCESS_MESSAGE;
  }
}
